import { useEffect, useState } from "react";
import Calendar from "react-calendar";
import { useNavigate } from "react-router";
import { diaryService } from "@/services/diaryService";
import { useAuth } from "@/context/AuthContext";
import { RecordCard } from "@/components/dataDisplay/cards/RecordCard";
import { getEmotionById, Emotion } from "@/constants/emotions";
import "react-calendar/dist/Calendar.css";
import "./styles.css";

interface DiaryEntry {
  id: string;
  audioUrl: string;
  createdAt: string;
  description: string;
  advice: string;
  feeling: string;
  userId: string;
}

export function CustomCalendar() {
  const { token } = useAuth();
  const navigate = useNavigate();

  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date>(today);
  const [entriesByDate, setEntriesByDate] = useState<
    Record<string, DiaryEntry[]>
  >({});
  const [currentMonth, setCurrentMonth] = useState<{
    year: number;
    month: number;
  }>({
    year: today.getFullYear(),
    month: today.getMonth() + 1,
  });

  const fetchEntriesByMonth = async (year: number, month: number) => {
    try {
      const response = await diaryService.getEntriesByMonth(
        token!,
        year,
        month
      );
      const grouped: Record<string, DiaryEntry[]> = {};

      response.forEach((entry: DiaryEntry) => {
        const dateKey = entry.createdAt.split("T")[0];
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(entry);
      });

      setEntriesByDate(grouped);
    } catch (err) {
      console.error("Error loading records by month:", err);
    }
  };

  const handleDayChange = (date: Date) => {
    setSelectedDate(date);
  };

  const handleActiveStartDateChange = ({
    activeStartDate,
  }: {
    activeStartDate: Date;
  }) => {
    setCurrentMonth({
      year: activeStartDate.getFullYear(),
      month: activeStartDate.getMonth() + 1,
    });
    fetchEntriesByMonth(
      activeStartDate.getFullYear(),
      activeStartDate.getMonth() + 1
    );
  };

  useEffect(() => {
    fetchEntriesByMonth(currentMonth.year, currentMonth.month);
  }, []);

  const formattedSelectedDate = selectedDate.toISOString().split("T")[0];
  const selectedEntries = entriesByDate[formattedSelectedDate] || [];

  const isSelectedDatePast = selectedDate < new Date();
  const isSelectedToday =
    formattedSelectedDate === today.toISOString().split("T")[0];
  const hasNoEntries = selectedEntries.length === 0;

  return (
    <div style={{ margin: "1rem 0" }}>
      <Calendar
        onChange={(value) => handleDayChange(value as Date)}
        onActiveStartDateChange={(value) =>
          handleActiveStartDateChange({
            activeStartDate: value.activeStartDate as Date,
          })
        }
        className={"custom-calendar"}
        value={selectedDate}
        showNavigation={true}
        tileContent={({ date }) => {
          const dateKey = date.toISOString().split("T")[0];
          const entries = entriesByDate[dateKey];
          if (entries && entries.length > 0) {
            return <div className="has-entries">•</div>;
          }
          return null;
        }}
        tileClassName={({ date }) => {
          const dateKey = date.toISOString().split("T")[0];
          if (formattedSelectedDate === dateKey) {
            return "selected-day";
          }
          return "";
        }}
      />

      {(selectedEntries.length > 0 ||
        isSelectedDatePast ||
        isSelectedToday) && (
        <div style={{ marginTop: "1rem" }}>
          <h3 style={{ color: "white" }}>
            Registros – {formatDate(formattedSelectedDate)}
          </h3>
          {hasNoEntries ? (
            <p style={{ color: "#ccc" }}>
              No hay registros en este día.{" "}
              <span
                style={{
                  color: "#F7B32B",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => navigate("/records/daily-intro")}
              >
                Agrega uno ahora.
              </span>
            </p>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: 16,
              }}
            >
              {selectedEntries.map((entry) => {
                const emotionData = getEmotionById(
                  entry.feeling as Emotion["id"]
                );
                if (!emotionData) return null;
                return (
                  <RecordCard
                    key={entry.id}
                    comment={entry.description}
                    advice={entry.advice}
                    date={new Date(entry.createdAt).toLocaleString("es-ES", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    emotion={emotionData?.label || entry.feeling}
                    icon={
                      <emotionData.Icon style={{ width: 24, height: 24 }} />
                    }
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const formatDate = (dateString: string) => {
  const parts = dateString.split("-");
  const date = new Date(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2])
  );
  return date.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};
